import { useContext } from 'react';
import { UnleashContext } from './components/Context';
import { FeatureFlag } from './constants';
import { UnleashContextState } from './types';

export const useFeatureFlags = (): UnleashContextState => {
  const context = useContext(UnleashContext);

  if (context === undefined) {
    throw Error(
      'UnleashContext must be used inside UnleashContext Provider scope'
    );
  }

  return context;
};

export const useFeatureToggle = (
  TOGGLE: FeatureFlag
): { isEnabled: boolean; isLoading: boolean } => {
  const { features, isLoading } = useFeatureFlags();

  return { isEnabled: !!features && !!features[TOGGLE]?.enabled, isLoading };
};

/**
 * The variant the proxy chose for this user, for flags rolled out as an A/B rather than a switch.
 *
 * `variant` is only reported while the flag is enabled AND the proxy assigned a variant; a flag that is
 * off, unknown to the proxy, or enabled without variants answers `undefined`, so a caller that compares
 * against one named arm lands every other case in control without a second check.
 *
 * An arm is only an arm when the proxy decided it from the user the experiment splits on, so the answer is
 * still loading until the features in hand were evaluated with an Unleash `userId`. Without a userId the
 * proxy picks a variant at random, and a random arm shown for one render is a control user in front of the
 * experiment. Switches are unaffected: `useFeatureToggle` reads the same features straight away.
 */
export const useFeatureVariant = (
  TOGGLE: FeatureFlag
): { variant: string | undefined; isEnabled: boolean; isLoading: boolean } => {
  const { features, isLoading, userId } = useFeatureFlags();

  if (isLoading || !userId) {
    return { variant: undefined, isEnabled: false, isLoading: true };
  }

  const feature = features?.[TOGGLE];
  const isEnabled = !!feature?.enabled;
  const variant =
    isEnabled && feature?.variant?.enabled ? feature.variant.name : undefined;

  return { variant, isEnabled, isLoading };
};
